import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of, forkJoin, throwError, combineLatest } from 'rxjs';
import { catchError, combineLatestWith, filter, first, map, switchMap, tap } from 'rxjs/operators';
import { LayoutService } from './layout.service';
import { PropertyService } from '../../common/services/property.service';
import { PageResolverResopnse } from '../types/page-resolver-response.type';
import { RouteUtils } from '../../common/utils/route.utils';
import { Property } from '../../common/types/property.type';
import { HttpResponse } from '@angular/common/http';
import { JsendResponse } from '../../common/types/jsend.response.type';
import { GlobalService } from '../../common/services/global.service';
import { PageBuilderService } from './page-builder.service';
import { cloneDeep } from 'lodash';
import { REQUEST, RESPONSE } from '../../../express.token';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { notFoundPageLayoutResponse } from '../../common/data/not-found-page.data';
import { CommonHelper } from '../../common/helpers/commonHelper';
import { ListingPageConfig } from '../../common/types/global-settings';
import { DEFAULT_LISTING_PAGE_CONFIG } from '../../common/data/listing-page-config.data';
import { environment } from '../../../environments/environment';
import { QuickSearchService } from '../../common/services/quick-search.service';
import { COMPONENT_MAPPING } from '../data/component-map.data';
import { Section } from '../types/section.type';

@Injectable({
    providedIn: 'root',
})
export class PageLayoutService {
    private layoutService = inject(LayoutService);
    private propertyService = inject(PropertyService);
    private pageBuilderService = inject(PageBuilderService);
    private res = inject(RESPONSE, { optional: true });
    private platformId = inject(PLATFORM_ID);
    private router = inject(Router);
    private globalService = inject(GlobalService);
    private quickSearchService = inject(QuickSearchService)

    /**
     * Default configuration for property details page sections
     */
    private defaultSectionConfig: ListingPageConfig = DEFAULT_LISTING_PAGE_CONFIG;
    private expressReq = inject(REQUEST, { optional: true });

    /**
     * Resolve page layout
     * @param route
     * @returns
     */
    resolvePageLayout(route: ActivatedRouteSnapshot): Observable<PageResolverResopnse | null> {
        const path = route.url.map((segment) => segment.path);
        let pagePath = path.join('/');
        const pageData = this.pageBuilderService.pageData.value;

        let clientDomain = environment.client_domain;
    
        if (isPlatformServer(this.platformId) && environment.production) {
            clientDomain = this.expressReq?.headers?.host!;
        }
    
        if (isPlatformBrowser(this.platformId) && environment.production) {
            clientDomain = window.location.origin;
        }

        if (RouteUtils.isPropertyDetailsPath(path, clientDomain)) {
            // Preload property details related components
            import('../../components/property-listing/property-listing.component');
            import('../../pages/page-layouts/property-page-layout/property-page-layout.component');
            import(
                '../../components/property-modal/property-modal.component'
            )

            return this.getPropertyDataFromSlug(path);
        }

        // If it's not a property details path, return the layout data directly
        return forkJoin({
            propertyDetailsResponse: of(null),
            pageDataResponse: pageData
                ? of(cloneDeep(pageData))
                : this.layoutService.getPageLayout(pagePath),
            companyDetails: this.globalService.companyDetails.pipe(
                filter((details) => details !== null),
                first(),
            ),
        }).pipe(
          tap((response) => {
                this.preloadJavascriptBundles(response.pageDataResponse?.body?.data);
                this.pageBuilderService.pageData.next(null);
                // Reset per-page mixed-list toggle on every navigation, then honor the
                // backend flag for community pages that opted in.
                this.globalService.includeClosedListings.next(
                    !!response.pageDataResponse?.body?.data?.include_closed_listings,
                );
            }),
        );
    }

    /*
        * Preload javascript bundles for components used in the page
    */
    private preloadJavascriptBundles(pageData: any) {
        if (pageData?.page_type === 'listing-page') {
            import('../../pages/page-layouts/property-page-layout/property-page-layout.component');
        }
        const pageSection = pageData.page.page_sections as Section[];
        const components = pageSection.map(section => section.blocks?.flat().map(block => block.children?.[0]?.component));
        const uniqueComponents = Array.from(new Set(components.flat()));
        uniqueComponents.forEach((component) => {
            if (component) {
                const componentDynamicLoader = COMPONENT_MAPPING[component];
                if (componentDynamicLoader) {
                    componentDynamicLoader();
                }
            }
        })
    }

    /**
     * Resolve page layout
     * @param route
     * @returns
     */
    resolveBlogHomeLayout(route: ActivatedRouteSnapshot): Observable<PageResolverResopnse | null> {
        const path = route.url.map((segment) => segment.path);
        let pagePath = path.join('/');

        return forkJoin({
            propertyDetailsResponse: of(null),
            pageDataResponse: this.layoutService.getBlogHomeLayout(pagePath),
            companyDetails: this.globalService.companyDetails.pipe(
                filter((details) => details !== null),
                first(),
            ),
        });
    }

    /**
     * Get data from slug
     * @param path
     * @returns
     */
    private getPropertyDataFromSlug(path: string[]): Observable<PageResolverResopnse | null> {
        const slug = encodeURIComponent(path[1]);
        const queryParams: any = {};
        if (!this.globalService.propertyDetailsConfig.value) {
            queryParams.fetch_listing_page_config = 'yes';
        }
        return this.propertyService.getPropertyBySlug(slug, queryParams).pipe(
            tap((response) => {
                let propertyDetailPageConfig = response.body?.data?.listing_page_config?.value;
                const metaTemplateDefaults = response.body?.data?.meta_template_defaults;
                if (queryParams.fetch_listing_page_config === 'yes') {
                    if (propertyDetailPageConfig) {
                        const metaTagsTemplate = propertyDetailPageConfig.meta_tags_template || {};
                        propertyDetailPageConfig = {
                            ...this.defaultSectionConfig,
                            ...propertyDetailPageConfig,
                            meta_tags_template: {
                                meta_title: metaTagsTemplate.meta_title || metaTemplateDefaults?.meta_title || '',
                                meta_description:
                                    metaTagsTemplate.meta_description || metaTemplateDefaults?.meta_description || '',
                            },
                        };
                        this.globalService.propertyDetailsConfig.next(propertyDetailPageConfig);
                        return;
                    }
                    this.globalService.propertyDetailsConfig.next({
                        ...this.defaultSectionConfig,
                        meta_tags_template: metaTemplateDefaults,
                    });
                }
            }),
            combineLatestWith(
                this.propertyService.getPropertiesViewsAndSaveCount(slug),
                this.globalService.companyDetails.pipe(
                    filter((details) => details !== null),
                    first(),
                ),
            ),
            switchMap(([propertyResponse, viewAndCountResponse, _]) => {
                return this.handlePropertyResponse(propertyResponse, viewAndCountResponse);
            }),
            catchError((error) => {
                console.error('Error fetching property by slug', error);
                if (error.status === 404) {
                    const notfoundPageData = CommonHelper.getNotFoundPageData();
                    if (isPlatformServer(this.platformId)) {
                        this.res?.status(404);
                    }
                    this.globalService.isNotFoundPage.next(false);
                    return of({
                        propertyDetailsResponse: null,
                        pageDataResponse: notfoundPageData as any,
                        companyDetails: this.globalService.companyDetails.pipe(
                            filter((details) => details !== null),
                            first(),
                        ),
                    });
                }
                return of(null);
            }),
        );
    }

    /**
     * Handle property response
     * @param propertyResponse
     * @returns
     */
    private handlePropertyResponse(
        propertyResponse: HttpResponse<JsendResponse>,
        viewAndCountResponse: HttpResponse<JsendResponse>,
    ): Observable<PageResolverResopnse> {
        const pageData = this.pageBuilderService.pageData.value;
        const property: Property = propertyResponse?.body?.data.property;
        if (property) {
            property.total_views =
                viewAndCountResponse?.body?.data?.total_views || 0;
            property.total_saves =
                viewAndCountResponse?.body?.data?.total_saves || 0;
        }
        if (!property) {
            return of(null);
        }

        const isClosedProperty =
            property?.standard_status?.toLowerCase() === 'closed' ||
            propertyResponse.body?.data.closed;
        const isDeleted = property?.is_deleted || propertyResponse.body?.data.is_deleted;

        if ((isDeleted || isClosedProperty) && !(this.globalService.closedPropertiesEnabled.value ?? false)) {
            const pageNotFoundData = CommonHelper.getNotFoundPageData(property);
            return of({
                propertyDetailsResponse: null,
                pageDataResponse: pageNotFoundData as any,
            });
        }

        if (propertyResponse.body?.data.redirect_url) {
            if (isPlatformServer(this.platformId)) {
                this.res?.redirect(301, propertyResponse.body.data.redirect_url);
            } else {
                this.router.navigateByUrl(propertyResponse.body.data.redirect_url);
            }
            return of(null);
        }
        if (isPlatformServer(this.platformId) && this.res) {
            this.res.locals = { ...this.res.locals, property };
        }

        const operatingStateSettings = this.globalService.companyDetails?.value?.operating_state_settings;
        const defaultState =
            operatingStateSettings?.default_state?.toLowerCase() ===
            property?.state_code?.toLowerCase();
        
        const includeStateSlug = !defaultState || operatingStateSettings?.multiple_states;

        const citySlug = includeStateSlug
                ? `/${CommonHelper.urlFriendlyString(`${property?.city}`)}-${property?.state_code?.toLowerCase()}`
                : `/${CommonHelper.urlFriendlyString(`${property?.city}`)}`;

        // When the property's city has a dedicated landing page (city_url is set),
        // render that page from the slug above. Otherwise fall back to the generic
        // homes search, scoped to the property's city + state so the listings/map
        // behind the modal show that city instead of every listing.
        const locations = [{ city: `${property?.city}`, state: `${property?.state_code}` }];
        const backgroundPage$ = property?.city_url
            ? this.layoutService.getPageLayout(citySlug)
            : this.layoutService
                  .getPageLayout('homes')
                  .pipe(map((response) => this.scopeListingSectionsToLocations(response, locations)));

        // The city-scoped fallback above is only reached when `backgroundPage$` is
        // actually used, i.e. there is no existing page to reuse. When `pageData`
        // is set (in-app navigation, e.g. clicking a card on /homes) the page the
        // user came from is reused as-is and never scoped — so the city filter must
        // NOT leak into the search bar or the closing navigation. Record whether
        // scoping happened so consumers gate on this instead of `city_url`.
        const scopedToCity = !pageData && !property?.city_url;

        return forkJoin({
            propertyDetailsResponse: of(propertyResponse),
            pageDataResponse: pageData ? of(cloneDeep(pageData)) : backgroundPage$,
        }).pipe(
            map((result) => ({
                ...result,
                backgroundLocations: scopedToCity ? locations : undefined,
            })),
            tap(() => {
                this.pageBuilderService.pageData.next(null);
            }),
        );
    }

    /**
     * Scope a page layout's property-listing sections to the given locations.
     *
     * Used for the generic "homes" fallback rendered behind a property modal when the
     * property's city has no dedicated landing page: the listing/stats blocks read their
     * filters from `inputs.options.search_params`, so injecting `locations` there makes
     * the background listings and map match the property's city. The response body is
     * deep-cloned so the layout cache (shareReplay) is not mutated across properties.
     */
    private scopeListingSectionsToLocations(
        response: HttpResponse<JsendResponse>,
        locations: { city: string; state: string }[],
    ): HttpResponse<JsendResponse> {
        const body = cloneDeep(response.body);
        const sections: any[] = body?.data?.page?.page_sections ?? [];
        sections.forEach((section) => {
            section?.blocks?.forEach((block: any) => {
                block?.children?.forEach((child: any) => {
                    if (
                        child?.component === 'property-listing' ||
                        child?.component === 'property-listing-stats'
                    ) {
                        child.inputs = child.inputs || {};
                        child.inputs.options = child.inputs.options || {};
                        child.inputs.options.search_params = {
                            ...child.inputs.options.search_params,
                            locations,
                        };
                    }
                });
            });
        });
        return response.clone({ body });
    }
}
